import { Button, Input, Modal, Tag, Typography, notification } from 'antd';
import configurationApi from 'api/configurationApi';
import payrollDashboardApi from 'api/payrollDashboardApi';
import recruitmentAiApi, {
  AICompleteJobSetup,
  AICompleteJobSetupResponse,
} from 'api/recruitmentAiApi';
import AiOutputLanguageSelector, {
  AiOutputLanguage,
  getDefaultAiOutputLanguage,
} from 'features/recruitment/components/AiOutputLanguageSelector';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ClarificationAnswer {
  id: string;
  question: string;
  answer: string;
}

interface Props {
  visible: boolean;
  onCancel: () => void;
  onCreated: (jobId: string) => void;
}

const allowedCandidateStatuses = [
  'Application Received',
  'Shortlisted',
  'In Progress',
  'Job Offer',
  'Offer Accepted',
  'Offer Rejected',
  'Closed',
];

const OTHER_CHOICE = '__other__';

const AiCompleteJobSetupModal = ({ visible, onCancel, onCreated }: Props) => {
  const { t, i18n } = useTranslation(['recruitment']);
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [result, setResult] = useState<AICompleteJobSetupResponse>();
  const [answeredClarifications, setAnsweredClarifications] = useState<ClarificationAnswer[]>([]);
  const [selectedChoices, setSelectedChoices] = useState<Record<string, string>>({});
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>({});
  const [language, setLanguage] = useState<AiOutputLanguage>(() =>
    getDefaultAiOutputLanguage(i18n.language),
  );
  const [masterData, setMasterData] = useState({
    locations: [] as Array<{ id: number; name: string }>,
    departments: [] as Array<{ id: string; name: string }>,
    jobTypes: [] as Array<{ id: number; name: string }>,
  });
  const [loadingMasterData, setLoadingMasterData] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setPrompt('');
    setMessages([]);
    setResult(undefined);
    setAnsweredClarifications([]);
    setSelectedChoices({});
    setCustomAnswers({});
    setLanguage(getDefaultAiOutputLanguage(i18n.language));
    setLoadingMasterData(true);
    Promise.all([
      payrollDashboardApi.getLocation(),
      payrollDashboardApi.getProject(),
      configurationApi.getJobType(),
    ])
      .then(([locations, departments, jobTypes]: any[]) => {
        setMasterData({
          locations: (locations?.data?.result || []).map((item: any) => ({
            id: Number(item.id),
            name: item.name,
          })),
          departments: (departments?.data?.result || []).map((item: any) => ({
            id: item.projectCode,
            name: item.name,
          })),
          jobTypes: (jobTypes?.data?.result || []).map((item: any) => ({
            id: Number(item.id),
            name: item.name,
          })),
        });
      })
      .finally(() => setLoadingMasterData(false));
  }, [i18n.language, visible]);

  const isClarifying = result?.status === 'needs_clarification';
  const isReady = result?.status === 'ready' && Boolean(result.setup);
  const pendingQuestions = isClarifying ? result.clarificationQuestions : [];
  const getSelectedAnswer = (id: string) => {
    const selected = selectedChoices[id];
    return selected === OTHER_CHOICE ? customAnswers[id]?.trim() : selected?.trim();
  };
  const clarificationComplete =
    pendingQuestions.length > 0 && pendingQuestions.every((item) => getSelectedAnswer(item.id));

  const sendMessage = async () => {
    const newAnswers: ClarificationAnswer[] = isClarifying
      ? pendingQuestions.map((item) => ({
          id: item.id,
          question: item.question,
          answer: getSelectedAnswer(item.id) || '',
        }))
      : [];
    const content = isClarifying
      ? newAnswers.map((item) => `${item.question}: ${item.answer}`).join('\n')
      : prompt.trim();
    if (!content || generating || loadingMasterData) return;

    const answeredIds = new Set(newAnswers.map((item) => item.id));
    const nextAnsweredClarifications = [
      ...answeredClarifications.filter((item) => !answeredIds.has(item.id)),
      ...newAnswers,
    ];

    const nextMessages: Message[] = [...messages, { role: 'user', content }];
    setMessages(nextMessages);
    setPrompt('');
    setGenerating(true);
    try {
      const requestContext = {
        conversation: nextMessages,
        answeredClarifications: isReady ? [] : nextAnsweredClarifications,
        availableLocations: masterData.locations,
        availableDepartments: masterData.departments,
        availableJobTypes: masterData.jobTypes,
        allowedCandidateStatuses,
        allowedEventTypes: ['Interview', 'Test', 'Other'],
      };
      let response = await recruitmentAiApi.planCompleteJobSetup({
        prompt: content,
        options: { language },
        context: requestContext,
      });
      const answeredIdSet = new Set(nextAnsweredClarifications.map((item) => item.id));
      const repeatedAnsweredQuestions =
        response.status === 'needs_clarification' &&
        response.clarificationQuestions.length > 0 &&
        response.clarificationQuestions.every((item) => answeredIdSet.has(item.id));
      if (repeatedAnsweredQuestions) {
        response = await recruitmentAiApi.planCompleteJobSetup({
          prompt:
            'All returned clarification ids already have authoritative HR answers. Use them and complete the setup without asking those ids again.',
          options: { language },
          context: requestContext,
        });
      }
      setResult(response);
      setMessages([...nextMessages, { role: 'assistant', content: response.assistantMessage }]);
      setAnsweredClarifications(nextAnsweredClarifications);
      if (response.status === 'needs_clarification') {
        const answerById = new Map(
          nextAnsweredClarifications.map((item) => [item.id, item.answer]),
        );
        const nextSelectedChoices: Record<string, string> = {};
        const nextCustomAnswers: Record<string, string> = {};
        response.clarificationQuestions.forEach((item) => {
          const previousAnswer = answerById.get(item.id);
          if (!previousAnswer) return;
          if (item.choices.includes(previousAnswer)) {
            nextSelectedChoices[item.id] = previousAnswer;
          } else {
            nextSelectedChoices[item.id] = OTHER_CHOICE;
            nextCustomAnswers[item.id] = previousAnswer;
          }
        });
        setSelectedChoices(nextSelectedChoices);
        setCustomAnswers(nextCustomAnswers);
      } else {
        setSelectedChoices({});
        setCustomAnswers({});
      }
    } finally {
      setGenerating(false);
    }
  };

  const createCompleteJob = async () => {
    if (!result?.setup) return;
    setCreating(true);
    try {
      const created = await recruitmentAiApi.createCompleteJobSetup(result.setup);
      notification.success({
        message: t('recruitment:ai_complete_job_created'),
        description: `${created.job.jobCode} · ${result.setup.job.positionName}`,
      });
      onCreated(created.job.id);
    } finally {
      setCreating(false);
    }
  };

  const selectExample = (value: string) => setPrompt(value);

  return (
    <Modal
      visible={visible}
      width={920}
      centered
      maskClosable={false}
      title={t('recruitment:ai_complete_job_title')}
      footer={null}
      onCancel={generating || creating ? undefined : onCancel}
      destroyOnClose
    >
      <Paragraph type="secondary">{t('recruitment:ai_complete_job_description')}</Paragraph>
      <LanguageRow>
        <AiOutputLanguageSelector
          value={language}
          onChange={setLanguage}
          disabled={generating || creating}
        />
      </LanguageRow>

      <Conversation>
        {messages.length === 0 ? (
          <Intro>
            <Text>{t('recruitment:ai_complete_job_prompt_tip')}</Text>
            <Text type="secondary">{t('recruitment:ai_complete_job_examples')}</Text>
            <ExampleButton
              onClick={() => selectExample(t('recruitment:ai_complete_job_example_one') as string)}
            >
              {t('recruitment:ai_complete_job_example_one')}
            </ExampleButton>
            <ExampleButton
              onClick={() => selectExample(t('recruitment:ai_complete_job_example_two') as string)}
            >
              {t('recruitment:ai_complete_job_example_two')}
            </ExampleButton>
          </Intro>
        ) : (
          messages.map((message, index) => (
            <MessageBubble key={`${message.role}-${index}`} roleName={message.role}>
              <Text strong>
                {message.role === 'user'
                  ? t('recruitment:ai_complete_job_you')
                  : t('recruitment:ai_complete_job_assistant')}
              </Text>
              <div>{message.content}</div>
            </MessageBubble>
          ))
        )}

        {result?.status === 'needs_clarification' && (
          <Clarification>
            <Text strong>{t('recruitment:ai_complete_job_need_more')}</Text>
            {result.clarificationQuestions.map((item, index) => (
              <Question key={item.id}>
                <QuestionTitle>
                  {index + 1}. {item.question}
                </QuestionTitle>
                <Text type="secondary">{item.reason}</Text>
                <ChoiceList>
                  {item.choices.map((choice) => (
                    <ChoiceTag
                      key={choice}
                      className={selectedChoices[item.id] === choice ? 'is-selected' : undefined}
                      onClick={() =>
                        setSelectedChoices((values) => ({ ...values, [item.id]: choice }))
                      }
                    >
                      {choice}
                    </ChoiceTag>
                  ))}
                  <ChoiceTag
                    className={
                      selectedChoices[item.id] === OTHER_CHOICE ? 'is-selected' : undefined
                    }
                    onClick={() =>
                      setSelectedChoices((values) => ({
                        ...values,
                        [item.id]: OTHER_CHOICE,
                      }))
                    }
                  >
                    {t('recruitment:ai_complete_job_other_choice')}
                  </ChoiceTag>
                </ChoiceList>
                {selectedChoices[item.id] === OTHER_CHOICE && (
                  <CustomAnswerInput
                    value={customAnswers[item.id] || ''}
                    onChange={(event) =>
                      setCustomAnswers((values) => ({
                        ...values,
                        [item.id]: event.target.value,
                      }))
                    }
                    placeholder={t('recruitment:ai_complete_job_other_placeholder')}
                    maxLength={500}
                    disabled={generating}
                  />
                )}
              </Question>
            ))}
          </Clarification>
        )}
      </Conversation>

      {isReady && result?.setup && <SetupReview setup={result.setup} />}

      {isClarifying ? (
        <ClarificationActions>
          <Text type="secondary">
            {t('recruitment:ai_complete_job_selected_count', {
              selected: pendingQuestions.filter((item) => getSelectedAnswer(item.id)).length,
              total: pendingQuestions.length,
            })}
          </Text>
          <PrimaryButton
            loading={generating}
            disabled={!clarificationComplete || creating}
            onClick={sendMessage}
          >
            {t('recruitment:ai_complete_job_continue')}
          </PrimaryButton>
        </ClarificationActions>
      ) : (
        <PromptArea>
          <TextArea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder={
              messages.length
                ? t('recruitment:ai_complete_job_reply_placeholder')
                : t('recruitment:ai_complete_job_placeholder')
            }
            rows={3}
            maxLength={5000}
            showCount
            disabled={generating || creating || loadingMasterData}
            onPressEnter={(event) => {
              if (!event.shiftKey) {
                event.preventDefault();
                sendMessage();
              }
            }}
          />
          <PromptActions>
            <Text type="secondary">
              {isReady
                ? t('recruitment:ai_complete_job_adjust_tip')
                : t('recruitment:ai_complete_job_review_tip')}
            </Text>
            <PrimaryButton
              loading={generating || loadingMasterData}
              disabled={!prompt.trim() || creating}
              onClick={sendMessage}
            >
              {messages.length
                ? t('recruitment:ai_complete_job_continue')
                : t('recruitment:ai_complete_job_start')}
            </PrimaryButton>
          </PromptActions>
        </PromptArea>
      )}

      <Footer>
        <Button disabled={generating || creating} onClick={onCancel}>
          {t('recruitment:cancel')}
        </Button>
        <PrimaryButton
          disabled={!isReady || generating}
          loading={creating}
          onClick={createCompleteJob}
        >
          {t('recruitment:ai_complete_job_create_action')}
        </PrimaryButton>
      </Footer>
    </Modal>
  );
};

const SetupReview = ({ setup }: { setup: AICompleteJobSetup }) => {
  const { t } = useTranslation(['recruitment']);
  const requirementsText = setup.job.requirements
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return (
    <Review>
      <Title level={5}>{t('recruitment:ai_complete_job_ready_title')}</Title>
      <JobFacts>
        <span>{setup.job.positionName}</span>
        <span>{setup.job.departmentName}</span>
        <span>{setup.job.locationName}</span>
        <span>{setup.job.jobTypeName}</span>
        <span>
          {t('recruitment:ai_complete_job_headcount_value', {
            count: setup.job.headcount,
          })}
        </span>
      </JobFacts>
      <ReviewGrid>
        <ReviewItem>
          <Text type="secondary">{t('recruitment:ai_complete_job_job_template')}</Text>
          <Text strong>{setup.job.name}</Text>
          <PreviewText>{requirementsText}</PreviewText>
        </ReviewItem>
        <ReviewItem>
          <Text type="secondary">{t('recruitment:ai_complete_job_question_pool')}</Text>
          <Text strong>{setup.questionPool.name}</Text>
          <CompactList>
            {setup.questionPool.questions.map((question, index) => (
              <li key={`${question}-${index}`}>{question}</li>
            ))}
          </CompactList>
        </ReviewItem>
        <ReviewItem>
          <Text type="secondary">{t('recruitment:ai_complete_job_interview_template')}</Text>
          <Text strong>{setup.interviewTemplate.name}</Text>
          <CompactList>
            {setup.interviewTemplate.competencies.map((competency) => (
              <li key={competency.name}>
                {competency.name}: {competency.minimumScore}–{competency.maximumScore}
              </li>
            ))}
          </CompactList>
        </ReviewItem>
        <ReviewItem>
          <Text type="secondary">{t('recruitment:ai_complete_job_workflow')}</Text>
          <Text strong>{setup.workflow.name}</Text>
          <CompactList>
            {setup.workflow.stages.map((stage, index) => (
              <li key={`${stage.name}-${index}`}>
                {index + 1}. {stage.name} · {stage.eventType} · {stage.statusCandidate}
              </li>
            ))}
          </CompactList>
        </ReviewItem>
      </ReviewGrid>
    </Review>
  );
};

export default AiCompleteJobSetupModal;

const LanguageRow = styled.div`
  margin-bottom: 16px;
`;

const Conversation = styled.div`
  max-height: 280px;
  padding: 16px;
  overflow-y: auto;
  border: 1px solid var(--cl_neutral100);
  background: var(--cl_neutral10, #fafafa);
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const ExampleButton = styled(Button)`
  height: auto;
  padding: 5px 10px;
  white-space: normal;
  text-align: left;
`;

const MessageBubble = styled.div<{ roleName: 'user' | 'assistant' }>`
  width: fit-content;
  max-width: 82%;
  padding: 9px 12px;
  margin: ${({ roleName }) => (roleName === 'user' ? '0 0 10px auto' : '0 auto 10px 0')};
  border: 1px solid
    ${({ roleName }) => (roleName === 'user' ? 'var(--cl_primary500)' : 'var(--cl_neutral100)')};
  border-radius: var(--bd_radius_default);
  background: var(--cl_white);

  > div:last-child {
    margin-top: 3px;
    white-space: pre-wrap;
  }
`;

const Clarification = styled.div`
  padding: 12px;
  border-left: 3px solid var(--cl_primary500);
  background: var(--cl_white);
`;

const Question = styled.div`
  padding: 12px 0;

  & + & {
    border-top: 1px solid var(--cl_neutral100);
  }
`;

const QuestionTitle = styled.div`
  margin-bottom: 2px;
  font-weight: 500;
`;

const ChoiceList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 8px;
`;

const ChoiceTag = styled(Tag)`
  padding: 5px 12px;
  margin: 0;
  color: var(--cl_neutral500);
  border: 1px solid var(--cl_neutral100);
  border-radius: var(--bd_radius_default);
  background: var(--cl_white);
  cursor: pointer;

  &:hover {
    color: var(--cl_primary500);
    border-color: var(--cl_primary500);
  }

  &.is-selected {
    color: var(--cl_primary600);
    border-color: var(--cl_primary500);
    background: rgba(241, 89, 42, 0.12);
    box-shadow: inset 0 0 0 1px var(--cl_primary500);
  }
`;

const CustomAnswerInput = styled(Input)`
  margin-top: 8px;
`;

const Review = styled.div`
  max-height: 390px;
  padding: 16px;
  margin-top: 16px;
  overflow-y: auto;
  border: 1px solid var(--cl_primary500);
  background: rgba(241, 89, 42, 0.03);

  .ant-typography {
    margin-bottom: 0;
  }
`;

const JobFacts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-top: 8px;
  color: var(--cl_neutral500);
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
`;

const ReviewItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  border: 1px solid var(--cl_neutral100);
  background: var(--cl_white);
`;

const PreviewText = styled.div`
  display: -webkit-box;
  margin-top: 6px;
  overflow: hidden;
  color: var(--cl_neutral500);
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
`;

const CompactList = styled.ul`
  padding-left: 18px;
  margin: 6px 0 0;
  color: var(--cl_neutral500);

  li + li {
    margin-top: 3px;
  }
`;

const PromptArea = styled.div`
  margin-top: 16px;
`;

const PromptActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 10px;
`;

const ClarificationActions = styled(PromptActions)`
  padding-top: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid var(--cl_neutral100);
`;

const PrimaryButton = styled(Button)`
  color: var(--cl_white);
  border-color: var(--cl_primary500);
  border-radius: var(--bd_radius_default);
  background-color: var(--cl_primary500);

  &:hover,
  &:focus {
    color: var(--cl_white);
    border-color: var(--cl_primary600);
    background-color: var(--cl_primary600);
  }
`;
